import type { DevChatDetail, DevOverview } from '#/services/dev-console/index.js'
import type { GroupSettings, GroupSettingsPatch } from '#/services/settings/schema.js'
import type { AiShieldStats, ChatStats } from '#/services/stats/service.js'
import type { AiShieldFlagsResponse } from '../routes/ai-shield-flags.js'

// The contract client and the SINGLE place the Authorization header is set. The page is served from the
// API origin, so requests use relative URLs (same-origin). Every failure becomes an ApiError the
// orchestrator surfaces LOUDLY — nothing is swallowed. The raw initData is sent on every request; the
// backend re-verifies it (HMAC, key "WebAppData") and re-authorizes the chat — the client is never
// trusted (DOSSIER §12.2/§12.3).

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(`${status} ${code}`)
    this.name = 'ApiError'
  }
}

/**
 * Map an error to a loud, human message key. ApiError statuses follow the backend contract
 * (401/403/429/400/503) and status 0 marks a network failure; the fallback covers an unexpected throw.
 *
 * Vive QUI, accanto ad ApiError, e non nell'orchestratore: dalla sessione 85 lo usa anche il selettore
 * dei gruppi, che è montato PRIMA dell'orchestratore e non può importarlo senza un ciclo. Prima era
 * privato in main.ts, e il selettore — nato senza — mostrava lo stesso messaggio generico più un
 * pulsante «Riprova» per QUALUNQUE causa: su un 429 quel pulsante significa ritentare dentro il
 * limite, su un 401 significa riprovare con un token scaduto. Un fatto in un posto solo.
 */
export function messageKey(err: unknown, fallback = 'ui-err-network'): string {
  if (!(err instanceof ApiError))
    return fallback
  if (err.status === 401)
    return 'ui-err-unauthorized'
  if (err.status === 403)
    return 'ui-err-forbidden'
  if (err.status === 429)
    return 'ui-err-rate-limited' // the F-10 limiter — "try again" now would retry INTO the limit, so say wait
  if (err.code === 'invalid_settings')
    return 'ui-err-invalid'
  if (err.code === 'bad_chat')
    return 'ui-err-bad-chat' // a 400 too, but the truthful cause is "I can't see that group", not "malformed"
  if (err.code === 'log_channel_unreachable')
    return 'ui-logchannel-unreachable' // a 400 too, but the truthful cause is "I couldn't post to that channel"
  // The other refusals of the one log-destination writer (defect (x), s106), each with its own truthful cause.
  // `log_channel_anonymous` has none: the web's actor is always a verified user, so it falls to bad-request.
  if (err.code === 'log_channel_unverifiable')
    return 'ui-logchannel-unverifiable'
  if (err.code === 'log_channel_not_admin')
    return 'ui-logchannel-not-admin'
  if (err.code === 'log_channel_superseded')
    return 'ui-logchannel-superseded'
  if (err.status === 400)
    return 'ui-err-bad-request'
  if (err.status === 0)
    return 'ui-err-network'
  return fallback
}

function authHeader(): Record<string, string> {
  const initData = window.Telegram?.WebApp.initData ?? ''
  return { Authorization: `tma ${initData}` }
}

// Opaque error bodies are exactly `{ error: code }` (the app's onError ALWAYS emits JSON, even on 401),
// so the happy path reads body.error. The catch only fires for a NON-backend response — e.g. an nginx/
// proxy 502 with an HTML body — where we fall back to an http_<status> code. (Network failures are status
// 0, set by the callers below.)
async function errorCode(res: Response): Promise<string> {
  try {
    const body = await res.json() as { error?: string }
    return body.error ?? `http_${res.status}`
  }
  catch {
    return `http_${res.status}`
  }
}

// Every settings-returning response (GET and PATCH /api/settings, POST /api/log-channel) carries
// `logDestination`, the log chat as the server's ROW holds it with its revision — the GET from the row it
// read, a write from the row it committed. The client takes the shown id and the revision TOGETHER from that
// field, and never `settings.logChannelId` (round 2 finding 4: a PATCH answer that moved the id alone left the
// screen showing one destination and holding the revision of another, so the next choice was refused).
export interface SettingsWithRevision {
  /** The settings, with `logChannelId` taken from the ROW (logDestination) — the one the section shows. */
  settings: GroupSettings
  /** The revision of the log destination shown — sent back with the next choice (setLogChannel). */
  logRevision: number
}

// The GET also carries the bound group's display `title` (so the dashboard shows which group it configures).
export interface LoadedSettings extends SettingsWithRevision {
  title: string
}

/** The log destination as the server's ROW holds it (every settings-returning response). */
interface LogDestinationBody {
  channelId: string | null
  revision: number
}

interface OkBody {
  settings: GroupSettings
  title?: string
  logDestination?: LogDestinationBody
}

async function parseOk(res: Response): Promise<OkBody> {
  if (!res.ok)
    throw new ApiError(res.status, await errorCode(res))
  return await res.json() as OkBody
}

// The log destination of a response that must carry one — loud when it doesn't: without the revision the
// next choice could not be conditional, and the section would show the cached id instead of the row's.
function logDestinationOf(body: OkBody): LogDestinationBody {
  const d = body.logDestination
  if (d === undefined || !Number.isInteger(d.revision))
    throw new ApiError(0, 'bad_response')
  return d
}

// The ONE place a response becomes what the screen keeps: the id shown and its revision, from the same field.
function reconciled(body: OkBody): SettingsWithRevision {
  const log = logDestinationOf(body)
  return { settings: { ...body.settings, logChannelId: log.channelId }, logRevision: log.revision }
}

export async function getSettings(chat: string): Promise<LoadedSettings> {
  let res: Response
  try {
    res = await fetch(`/api/settings?chat=${encodeURIComponent(chat)}`, { headers: authHeader() })
  }
  catch {
    throw new ApiError(0, 'network')
  }
  const body = await parseOk(res)
  // Reconciled to the ROW (round 1 finding 5): the cached settings can lag a write whose cache refresh failed,
  // and the log section must show the destination its revision describes.
  return {
    ...reconciled(body),
    title: body.title ?? '', // '' → the title row is skipped (defensive)
  }
}

// The shared READ path — the one place the GET network/error contract lives (the read sibling of
// send() below), so the read-only endpoints can't drift on it: fetch with the auth header, a network
// failure → ApiError(0, 'network'), a non-2xx → a coded ApiError, else the JSON body as T (cold-eye
// session 66 — this block used to be copy-pasted per caller).
async function getJson<T>(pathWithQuery: string): Promise<T> {
  let res: Response
  try {
    res = await fetch(pathWithQuery, { headers: authHeader() })
  }
  catch {
    throw new ApiError(0, 'network')
  }
  if (!res.ok)
    throw new ApiError(res.status, await errorCode(res))
  return await res.json() as T
}

// The read-only moderation-activity counts (GET /api/stats) — the raw ChatStats; bucketing + labels are
// the stats view's job (stats.ts renders from the shared services/stats/spec.ts). The type import is
// erased by esbuild (leaf module, no server deps in the bundle).
export async function getStats(chat: string): Promise<ChatStats> {
  return getJson(`/api/stats?chat=${encodeURIComponent(chat)}`)
}

// L'elenco dei gruppi che CHI CHIEDE può configurare (GET /api/my-groups). Il server verifica una per
// una le chat del roster contro l'identità dell'initData e OMETTE quelle non sue: qui arriva già
// filtrato, e il client non deve (né può) decidere cosa mostrare. `truncated` dice che l'elenco è
// PARZIALE perché qualche verifica non è riuscita (Telegram in affanno) — `unverified` le conta. Il
// nome viene dal tetto di 40 verifiche, tolto nella s93: ora il server le esamina tutte.
// `rosterOnly` arriva solo al proprietario (ADR 0007): il suo elenco è quello delle chat registrate, non tutte.
export async function getMyGroups(): Promise<{ groups: { chatId: string, title: string, type: string }[], truncated: boolean, unverified: number, rosterOnly?: boolean }> {
  return getJson('/api/my-groups')
}

// The read-only AI-Shield activity (GET /api/ai-shield-stats) — the raw AiShieldStats (per-category
// `ai-flag` counts, zero-filled + canonically ordered server-side).
export async function getAiShieldStats(chat: string): Promise<AiShieldStats> {
  return getJson(`/api/ai-shield-stats?chat=${encodeURIComponent(chat)}`)
}

// The AI-Shield drill-down page (GET /api/ai-shield-flags) — one keyset page of individual `ai-flag`
// events (the wire format is the ROUTE's contract, type-imported so the bundle stays server-free).
export async function getAiShieldFlags(chat: string, opts: { category?: string, cursor?: string } = {}): Promise<AiShieldFlagsResponse> {
  const q = new URLSearchParams({ chat })
  if (opts.category !== undefined)
    q.set('category', opts.category)
  if (opts.cursor !== undefined)
    q.set('cursor', opts.cursor)
  return getJson(`/api/ai-shield-flags?${q.toString()}`)
}

// The owner console reads (GET /api/dev/*) — owner-gated server-side (403 for anyone else; the view is
// only reachable from the owner's /dev button, but the URL is never the wall). Types imported from the
// service (type-only — erased, the bundle stays server-free).
export async function getDevOverview(): Promise<DevOverview> {
  return getJson('/api/dev/overview')
}

export async function getDevChat(chat: string): Promise<DevChatDetail> {
  return getJson(`/api/dev/chat?chat=${encodeURIComponent(chat)}`)
}

// The shared write path for the settings-returning mutations (PATCH /api/settings, POST /api/log-channel):
// send a JSON body, map a network failure to ApiError(0, 'network'), and parse the authoritative body out of
// the response (parseOk throws a coded ApiError on a non-2xx; reconciled() takes the id and its revision). One place for the network/error contract so
// the two callers can't drift on it. keepalive is the close-flush escape hatch (see patchSettings); off for
// every ordinary save.
async function send(path: string, method: 'PATCH' | 'POST', body: unknown, keepalive = false): Promise<OkBody> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive,
    })
  }
  catch {
    throw new ApiError(0, 'network')
  }
  return parseOk(res)
}

export async function patchSettings(chat: string, patch: GroupSettingsPatch, keepalive = false): Promise<SettingsWithRevision> {
  // keepalive lets a save started on the way out (main.ts flushPendingEdits, on BackButton/visibilitychange)
  // survive the webview teardown — so a "type then immediately close" edit isn't cancelled mid-flight. The
  // caller sets it ONLY for that close-flush path: keepalive also caps the request body at 64 KiB, and the
  // blocklist/allowlistDomains lists can legitimately serialize past that, so an ordinary in-app save must
  // run WITHOUT it — otherwise a large list would be rejected by the browser before it ever left the page.
  // The answer is the committed row's: the settings AND the log destination with its revision, which main.ts
  // adopts together (round 2 finding 4).
  return reconciled(await send('/api/settings', 'PATCH', { chat, patch }, keepalive))
}

// Set (channelId = a numeric id string) or clear (null) the group's log channel via the DEDICATED,
// self-verifying endpoint (POST /api/log-channel) — NOT PATCH /api/settings, which refuses the key: the backend
// checks that the admin also runs the destination, then test-posts there BEFORE saving, so a 200 means both
// held (setting) or the channel was cleared. A choice carries `expectedRevision`, the revision of the
// destination the dashboard SHOWS: if any choice committed since (another admin's «Disattiva», a newer choice),
// the server refuses `superseded` — a retry from a stale screen never switches the log back on (round 1
// finding 5). Returns the authoritative settings and the NEW revision, for main.ts to keep. Same loud error
// contract; each refusal (400 `log_channel_*`) is mapped to its own message in messageKey. No keepalive —
// this is never a close-flush path.
export async function setLogChannel(chat: string, channelId: string | null, expectedRevision: number): Promise<SettingsWithRevision> {
  return reconciled(await send('/api/log-channel', 'POST', { chat, channelId, expectedRevision }))
}
